import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  Building2,
  ChevronRight,
  Pencil,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select } from '../components/ui/Input.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { useToast } from '../components/ui/Toast.tsx';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string | null;
  status: string;
  createdAt: string;
}

interface TenantEditForm {
  name: string;
  plan: string;
  status: string;
}

export function TenantsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [editTenant, setEditTenant] = useState<Tenant | null>(null);
  const [archiveTenant, setArchiveTenant] = useState<Tenant | null>(null);
  const [restoreTenant, setRestoreTenant] = useState<Tenant | null>(null);
  const [deleteTenant, setDeleteTenant] = useState<Tenant | null>(null);
  const [deleteSlugInput, setDeleteSlugInput] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [editForm, setEditForm] = useState<TenantEditForm>({
    name: '',
    plan: '',
    status: 'active',
  });

  const { data: rawTenants, isLoading, isError, error } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get('/sa/tenants').then((r) => r.data),
  });
  const allTenants: Tenant[] = Array.isArray(rawTenants) ? rawTenants : [];
  const data = showArchived
    ? allTenants
    : allTenants.filter((t) => t.status !== 'archived');

  const isForbidden =
    isError &&
    (error as { response?: { status?: number } })?.response?.status === 403;

  const createTenant = useMutation({
    mutationFn: (body: { slug: string; name: string }) =>
      api.post('/sa/tenants', body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setShowCreate(false);
      setSlug('');
      setName('');
      toast.success('Tenant created and schema provisioned');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create tenant')),
  });

  const updateTenant = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<TenantEditForm> }) =>
      api.patch(`/sa/tenants/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setEditTenant(null);
      toast.success('Tenant updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update tenant')),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) =>
      api.patch(`/sa/tenants/${id}`, { status: 'archived' }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setArchiveTenant(null);
      toast.success('Tenant archived');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to archive tenant')),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) =>
      api.patch(`/sa/tenants/${id}`, { status: 'active' }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setRestoreTenant(null);
      toast.success('Tenant restored');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to restore tenant')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/sa/tenants/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setDeleteTenant(null);
      setDeleteSlugInput('');
      toast.success('Tenant permanently deleted');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to delete tenant')),
  });

  function openEdit(t: Tenant) {
    setEditForm({ name: t.name, plan: t.plan ?? 'standard', status: t.status });
    setEditTenant(t);
  }

  function statusBadgeVariant(status: string) {
    if (status === 'active') return 'success';
    if (status === 'suspended') return 'warning';
    if (status === 'archived') return 'default';
    return 'default';
  }

  const archivedCount = allTenants.filter((t) => t.status === 'archived').length;

  return (
    <div>
      <PageHeader
        title="Tenants"
        description="Super-admin: manage platform tenants and provision schemas"
        action={
          <div className="flex items-center gap-2">
            {archivedCount > 0 && (
              <button
                onClick={() => setShowArchived((v) => !v)}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                  showArchived
                    ? 'bg-zinc-100 border-zinc-300 text-zinc-700'
                    : 'border-zinc-200 text-zinc-400 hover:text-zinc-600 hover:border-zinc-300'
                }`}
              >
                {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
              </button>
            )}
            <Button size="sm" onClick={() => setShowCreate(true)}>
              + New tenant
            </Button>
          </div>
        }
      />

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {isForbidden && (
          <div className="flex items-start gap-3 px-5 py-4 bg-amber-50 border-b border-amber-100">
            <ShieldAlert size={16} className="text-amber-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Super-admin token required</p>
              <p className="text-xs text-amber-600 mt-0.5">
                Your current session is scoped to a tenant. Please log out and sign in again
                without selecting a tenant to obtain a super-admin token.
              </p>
            </div>
          </div>
        )}
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Slug
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Plan
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-4 py-3 w-28" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={3} cols={6} />
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState
                    icon={<Building2 size={26} />}
                    title="No tenants yet"
                    description="Create a tenant to provision a schema and start using the platform."
                    action={{ label: '+ New tenant', onClick: () => setShowCreate(true) }}
                  />
                </td>
              </tr>
            ) : (
              data.map((t) => (
                <tr
                  key={t.id}
                  className={`hover:bg-zinc-50/60 transition-colors group cursor-pointer ${
                    t.status === 'archived' ? 'opacity-50' : ''
                  }`}
                  onClick={() => navigate(`/admin/tenants/${t.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-zinc-900">{t.name}</td>
                  <td className="px-4 py-3">
                    <code className="text-xs font-mono text-zinc-500">{t.slug}</code>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{t.plan ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Badge variant={statusBadgeVariant(t.status)} dot>
                      {t.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {new Date(t.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {t.status !== 'archived' ? (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                            title="Edit"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setArchiveTenant(t); }}
                            className="p-1.5 text-zinc-400 hover:text-amber-600 hover:bg-amber-50 rounded-md"
                            title="Archive"
                          >
                            <Archive size={12} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setRestoreTenant(t); }}
                          className="p-1.5 text-zinc-400 hover:text-green-600 hover:bg-green-50 rounded-md"
                          title="Restore"
                        >
                          <ArchiveRestore size={12} />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteSlugInput('');
                          setDeleteTenant(t);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        title="Delete permanently"
                      >
                        <Trash2 size={12} />
                      </button>
                      <ChevronRight size={13} className="text-zinc-300" />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Tenant"
        description="Provisioning creates a dedicated Postgres schema for the tenant."
      >
        <div className="space-y-4">
          <Input
            label="Tenant name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Corp"
          />
          <Input
            label="Slug"
            hint="Used as the subdomain. Lowercase, hyphens only."
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder="acme-corp"
            className="font-mono"
          />
          {createTenant.isError && (
            <p className="text-xs text-red-500">{extractError(createTenant.error)}</p>
          )}
          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={createTenant.isPending}
              disabled={!slug || !name}
              onClick={() => createTenant.mutate({ slug, name })}
            >
              Create & provision
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editTenant}
        onClose={() => setEditTenant(null)}
        title={`Edit: ${editTenant?.name}`}
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={editForm.name}
            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
          />
          <Select
            label="Plan"
            value={editForm.plan}
            onChange={(e) => setEditForm((f) => ({ ...f, plan: e.target.value }))}
          >
            <option value="standard">Standard</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </Select>
          <Select
            label="Status"
            value={editForm.status}
            onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </Select>
          {updateTenant.isError && (
            <p className="text-xs text-red-500">{extractError(updateTenant.error)}</p>
          )}
          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => setEditTenant(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={updateTenant.isPending}
              disabled={!editForm.name}
              onClick={() =>
                editTenant && updateTenant.mutate({ id: editTenant.id, body: editForm })
              }
            >
              Save changes
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* Archive Modal */}
      <Modal
        open={!!archiveTenant}
        onClose={() => setArchiveTenant(null)}
        title={`Archive ${archiveTenant?.name}?`}
        description="The tenant will be hidden from the main list. No data will be deleted. You can restore it at any time."
      >
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setArchiveTenant(null)}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={archiveMutation.isPending}
            onClick={() => archiveTenant && archiveMutation.mutate(archiveTenant.id)}
          >
            Archive
          </Button>
        </ModalFooter>
      </Modal>

      {/* Restore Modal */}
      <Modal
        open={!!restoreTenant}
        onClose={() => setRestoreTenant(null)}
        title={`Restore ${restoreTenant?.name}?`}
        description="The tenant will be set back to active and visible in the main list."
      >
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setRestoreTenant(null)}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={restoreMutation.isPending}
            onClick={() => restoreTenant && restoreMutation.mutate(restoreTenant.id)}
          >
            Restore
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={!!deleteTenant}
        onClose={() => { setDeleteTenant(null); setDeleteSlugInput(''); }}
        title="Permanently delete tenant?"
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
            This will <strong>permanently destroy</strong> all data for{' '}
            <strong>{deleteTenant?.name}</strong>: customers, campaigns, loyalty accounts,
            message logs, and the entire Postgres schema. This action cannot be undone.
          </div>
          <Input
            label="Confirm by typing the tenant slug"
            hint={`Type "${deleteTenant?.slug}" to enable the delete button`}
            value={deleteSlugInput}
            onChange={(e) => setDeleteSlugInput(e.target.value)}
            placeholder={deleteTenant?.slug}
            className="font-mono"
          />
          {deleteMutation.isError && (
            <p className="text-xs text-red-500">{extractError(deleteMutation.error)}</p>
          )}
          <ModalFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setDeleteTenant(null); setDeleteSlugInput(''); }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="danger"
              loading={deleteMutation.isPending}
              disabled={deleteSlugInput !== deleteTenant?.slug}
              onClick={() => deleteTenant && deleteMutation.mutate(deleteTenant.id)}
            >
              Delete permanently
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
}
